import NetInfo from '@react-native-community/netinfo';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { journeyRepository } from '../database/JourneyRepository';
import { journeyApi } from '../api/journeyApi';
import type { LocalSyncLog } from '../database/schema';
import { logger } from '../../utils/logger';

/**
 * Offline Sync Manager
 *
 * Handles synchronization between local SQLite database and remote API.
 * Implements conflict resolution, retry logic, and background sync.
 */
export class OfflineSyncManager {
  private static instance: OfflineSyncManager;
  private isOnline = false;
  private isSyncing = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private retryTimeouts: Map<string, NodeJS.Timeout> = new Map();

  // Sync configuration
  private readonly SYNC_INTERVAL_MS = 30000; // 30 seconds
  private readonly MAX_RETRY_ATTEMPTS = 5;
  private readonly RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff

  private constructor() {
    // Defer network listener until start() to ensure DB init order
  }

  public static getInstance(): OfflineSyncManager {
    if (!OfflineSyncManager.instance) {
      OfflineSyncManager.instance = new OfflineSyncManager();
    }
    return OfflineSyncManager.instance;
  }

  /**
   * Initialize network connectivity listener
   */
  private initializeNetworkListener(): void {
    NetInfo.addEventListener(state => {
      const wasOnline = this.isOnline;
      this.isOnline = state.isConnected ?? false;

      if (!wasOnline && this.isOnline) {
        // Just came online, trigger sync
        logger.debug('Network connected, triggering sync');
        this.triggerSync();
      } else if (wasOnline && !this.isOnline) {
        // Just went offline
        logger.debug('Network disconnected');
        this.stopPeriodicSync();
      }
    });
  }

  /**
   * Start the sync manager
   */
  public async start(): Promise<void> {
    logger.debug('Starting OfflineSyncManager');

    // Initialize network connectivity listener on start
    this.initializeNetworkListener();

    // Check initial network state
    const netInfo = await NetInfo.fetch();
    this.isOnline = netInfo.isConnected ?? false;

    if (this.isOnline) {
      this.startPeriodicSync();
      this.triggerSync(); // Initial sync
    }
  }

  /**
   * Stop the sync manager
   */
  public stop(): void {
    logger.debug('Stopping OfflineSyncManager');
    this.stopPeriodicSync();
    this.clearAllRetryTimeouts();
  }

  /**
   * Start periodic background sync
   */
  private startPeriodicSync(): void {
    if (this.syncInterval) {
      return; // Already running
    }

    this.syncInterval = setInterval(() => {
      if (this.isOnline && !this.isSyncing) {
        this.triggerSync();
      }
    }, this.SYNC_INTERVAL_MS);
  }

  /**
   * Stop periodic sync
   */
  private stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Trigger a sync operation
   */
  public async triggerSync(): Promise<void> {
    if (!this.isOnline || this.isSyncing) {
      return;
    }

    this.isSyncing = true;
    logger.debug('Starting sync operation');

    try {
      // Get pending sync operations
      const pendingOps = await journeyRepository.getPendingSyncOperations();

      if (pendingOps.length === 0) {
        logger.debug('No pending sync operations');
        return;
      }

      logger.debug(`Processing ${pendingOps.length} pending sync operations`);

      // Process each operation
      for (const op of pendingOps) {
        await this.processSyncOperation(op);
      }

      // Update last sync timestamp
      await AsyncStorage.setItem('lastSyncAt', new Date().toISOString());
    } catch (error) {
      logger.error('Sync operation failed:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Process a single sync operation
   */
  private async processSyncOperation(op: LocalSyncLog): Promise<void> {
    try {
      // Mark as syncing
      await journeyRepository.updateSyncOperationStatus(op.id, 'syncing');

      switch (op.table_name) {
        case 'activity_progress':
          await this.syncActivityProgress(op);
          break;
        case 'journeys':
          await this.syncJourney(op);
          break;
        case 'journey_weeks':
          await this.syncJourneyWeek(op);
          break;
        case 'journey_activities':
          await this.syncJourneyActivity(op);
          break;
        default:
          logger.warn(`Unknown table for sync: ${op.table_name}`);
          return;
      }

      // Mark as completed
      await journeyRepository.updateSyncOperationStatus(op.id, 'completed');
      await journeyRepository.markAsSynced(op.table_name, op.record_id);

      logger.debug(`Sync completed for ${op.table_name}:${op.record_id}`);
    } catch (error) {
      logger.error(`Sync failed for ${op.table_name}:${op.record_id}:`, error);

      // Mark as failed and schedule retry
      await journeyRepository.updateSyncOperationStatus(
        op.id,
        'failed',
        error instanceof Error ? error.message : 'Unknown error'
      );

      this.scheduleRetry(op);
    }
  }

  /**
   * Sync activity progress
   */
  private async syncActivityProgress(op: LocalSyncLog): Promise<void> {
    const progress = await journeyRepository.getActivityProgress(
      'current_user',
      op.record_id
    );

    if (!progress) {
      logger.warn(`Activity progress not found: ${op.record_id}`);
      return;
    }

    switch (op.operation) {
      case 'create':
      case 'update':
        // For activity progress, we typically use the complete activity endpoint
        if (progress.status === 'completed') {
          await journeyApi.completeActivity(progress.activity_id, {
            time_spent_minutes: progress.time_spent_minutes,
            success_rate: progress.success_rate || 1.0,
            difficulty_rating: progress.difficulty_rating,
            engagement_score: progress.engagement_score || 0.8,
            notes: undefined, // Could be added to schema if needed
          });
        } else {
          // Update progress percentage
          await journeyApi.updateActivityProgress(
            progress.activity_id,
            progress.progress_percentage
          );
        }
        break;
      case 'delete':
        // Activity progress deletion is typically not supported
        logger.warn('Activity progress deletion not supported');
        break;
    }
  }

  /**
   * Sync journey (placeholder - journeys are typically read-only from mobile)
   */
  private async syncJourney(op: LocalSyncLog): Promise<void> {
    logger.debug(
      `Journey sync not implemented: ${op.operation} ${op.record_id}`
    );
    // Journeys are typically created and managed server-side
    // Mobile app usually only reads journey data
  }

  /**
   * Sync journey week (placeholder)
   */
  private async syncJourneyWeek(op: LocalSyncLog): Promise<void> {
    logger.debug(
      `Journey week sync not implemented: ${op.operation} ${op.record_id}`
    );
    // Journey weeks are typically managed server-side
  }

  /**
   * Sync journey activity (placeholder)
   */
  private async syncJourneyActivity(op: LocalSyncLog): Promise<void> {
    logger.debug(
      `Journey activity sync not implemented: ${op.operation} ${op.record_id}`
    );
    // Journey activities are typically managed server-side
    // Mobile might sync user ratings and feedback
  }

  /**
   * Schedule retry for failed sync operation
   */
  private scheduleRetry(op: LocalSyncLog): void {
    const retryCount = op.retry_count || 0;

    if (retryCount >= this.MAX_RETRY_ATTEMPTS) {
      logger.warn(
        `Max retry attempts reached for ${op.table_name}:${op.record_id}`
      );
      return;
    }

    const delay =
      this.RETRY_DELAYS[Math.min(retryCount, this.RETRY_DELAYS.length - 1)];

    const timeoutId = setTimeout(async () => {
      this.retryTimeouts.delete(op.id);

      if (this.isOnline) {
        await this.processSyncOperation(op);
      }
    }, delay);

    this.retryTimeouts.set(op.id, timeoutId);
    logger.debug(
      `Scheduled retry for ${op.table_name}:${op.record_id} in ${delay}ms`
    );
  }

  /**
   * Clear all retry timeouts
   */
  private clearAllRetryTimeouts(): void {
    for (const [id, timeout] of this.retryTimeouts) {
      clearTimeout(timeout);
    }
    this.retryTimeouts.clear();
  }

  /**
   * Force sync all pending operations
   */
  public async forceSyncAll(): Promise<void> {
    if (!this.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    await this.triggerSync();
  }

  /**
   * Get sync status
   */
  public async getSyncStatus(): Promise<{
    isOnline: boolean;
    isSyncing: boolean;
    lastSyncAt: string | null;
    pendingOperations: number;
  }> {
    const lastSyncAt = await AsyncStorage.getItem('lastSyncAt');
    const pendingOps = await journeyRepository.getPendingSyncOperations();

    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      lastSyncAt,
      pendingOperations: pendingOps.length,
    };
  }

  /**
   * Clear all sync data (for testing/reset)
   */
  public async clearSyncData(): Promise<void> {
    await AsyncStorage.removeItem('lastSyncAt');
    // Clear sync log is handled by database manager
  }
}

// Export singleton instance
export const offlineSyncManager = OfflineSyncManager.getInstance();
